import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { EmptyJournal } from "../components/EmptyStates";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BookOpen,
  Plus,
  FolderOpen,
  Pencil,
  Trash2,
  ImagePlus,
  X,
  ChevronLeft,
  Calendar,
  Loader2,
  FileText,
} from "lucide-react";

const Journal = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const autoOpenDone = useRef(false);

  const [form, setForm] = useState({
    trip_id: null,
    trip_name: "",
    title: "",
    content: "",
    date: new Date().toISOString().split("T")[0],
    photos: [],
  });

  useEffect(() => {
    const loadFolders = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/journal/trips`, { withCredentials: true });
        setFolders(res.data);
      } catch {
        toast.error("Failed to load journal folders");
      } finally {
        setLoading(false);
      }
    };
    loadFolders();
  }, []);

  // Auto-open editor if ?date= param is provided (from Calendar)
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam && !autoOpenDone.current && !loading) {
      autoOpenDone.current = true;
      setEditingEntry(null);
      setForm({
        trip_id: null,
        trip_name: "",
        title: "",
        content: "",
        date: dateParam,
        photos: [],
      });
      setShowEditor(true);
    }
  }, [searchParams, loading]);

  useEffect(() => {
    if (!activeFolder) return;
    const loadEntries = async () => {
      try {
        const params = activeFolder.trip_id
          ? { trip_id: activeFolder.trip_id }
          : { trip_name: activeFolder.trip_name };
        const res = await axios.get(`${API}/journal/entries`, { params, withCredentials: true });
        setEntries(res.data);
      } catch {
        toast.error("Failed to load entries");
      }
    };
    loadEntries();
  }, [activeFolder]);

  const fetchFolders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/journal/trips`, {
        withCredentials: true,
      });
      setFolders(res.data);
    } catch {
      toast.error("Failed to load journal folders");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const params = activeFolder.trip_id
        ? { trip_id: activeFolder.trip_id }
        : { trip_name: activeFolder.trip_name };
      const res = await axios.get(`${API}/journal/entries`, {
        params,
        withCredentials: true,
      });
      setEntries(res.data);
    } catch {
      toast.error("Failed to load entries");
    }
  };

  const openNewEntry = () => {
    setEditingEntry(null);
    setForm({
      trip_id: activeFolder?.trip_id || null,
      trip_name: activeFolder?.trip_name || "",
      title: "",
      content: "",
      date: new Date().toISOString().split("T")[0],
      photos: [],
    });
    setShowEditor(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setForm({
      trip_id: entry.trip_id,
      trip_name: entry.trip_name,
      title: entry.title,
      content: entry.content,
      date: entry.date,
      photos: entry.photos || [],
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.trip_name.trim() && !form.trip_id) {
      toast.error("Trip name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingEntry) {
        await axios.put(
          `${API}/journal/entries/${editingEntry.entry_id}`,
          form,
          { withCredentials: true }
        );
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/journal/entries`, form, {
          withCredentials: true,
        });
        toast.success("Entry created");
      }
      setShowEditor(false);
      fetchEntries();
      fetchFolders();
    } catch {
      toast.error("Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this journal entry?")) return;
    try {
      await axios.delete(`${API}/journal/entries/${entryId}`, {
        withCredentials: true,
      });
      toast.success("Entry deleted");
      fetchEntries();
      fetchFolders();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/journal/upload-photo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({
        ...prev,
        photos: [...prev.photos, res.data.url],
      }));
      toast.success("Photo added");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setForm((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== idx),
    }));
  };

  const openNewFolder = () => {
    setEditingEntry(null);
    setActiveFolder(null);
    setForm({
      trip_id: null,
      trip_name: "",
      title: "",
      content: "",
      date: new Date().toISOString().split("T")[0],
      photos: [],
    });
    setShowEditor(true);
  };

  // ── Folder List View ──
  if (!activeFolder) {
    return (
      <div
        className="page-container p-4 pt-6 pb-28"
        data-testid="journal-page"
      >
        <SEO title="Travel Journal" description="Keep a digital travel diary. Write journal entries organized by trip with photos and memories." path="/journal" />
        <div className="max-w-2xl mx-auto">
          {/* Hero Banner */}
          <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
            <img src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 flex items-end justify-between">
              <div>
                <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">Travel Journal</h1>
                <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Your travel memories</p>
              </div>
              <Button
                onClick={openNewFolder}
                className="btn-primary text-sm"
                data-testid="new-journal-entry-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Entry
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
            </div>
          ) : folders.length === 0 ? (
            <EmptyJournal onAction={openNewFolder} />
          ) : (
            <div className="space-y-2" data-testid="journal-folders">
              {folders.map((folder, i) => (
                <button
                  key={folder.trip_id || folder.trip_name || i}
                  onClick={() => setActiveFolder(folder)}
                  className="w-full flex items-center gap-4 p-4 bg-slate-900/70 hover:bg-slate-800/70 rounded-xl transition-all border border-transparent hover:border-amber-500/20"
                  data-testid={`journal-folder-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                    <FolderOpen className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="text-base font-semibold text-white truncate">
                      {folder.trip_name}
                    </h3>
                    <p className="text-xs text-slate-400">
                      {folder.entry_count}{" "}
                      {folder.entry_count === 1 ? "entry" : "entries"}
                      {folder.latest_date && ` · Last: ${folder.latest_date}`}
                    </p>
                  </div>
                  <ChevronLeft className="w-5 h-5 text-slate-500 rotate-180 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Editor Dialog */}
        <EditorDialog
          open={showEditor}
          onClose={() => setShowEditor(false)}
          form={form}
          setForm={setForm}
          onSave={handleSave}
          saving={saving}
          uploading={uploading}
          editingEntry={editingEntry}
          fileInputRef={fileInputRef}
          handlePhotoUpload={handlePhotoUpload}
          removePhoto={removePhoto}
          folders={folders}
        />
      </div>
    );
  }

  // ── Entries List View (inside a folder) ──
  return (
    <div
      className="page-container p-4 pt-6 pb-28"
      data-testid="journal-entries-page"
    >
      <SEO title="Travel Journal" description="Keep a digital travel diary. Write journal entries organized by trip with photos and memories." path="/journal" />
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => {
            setActiveFolder(null);
            setEntries([]);
          }}
          className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4 transition-colors"
          data-testid="journal-back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          All Trips
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">
              {activeFolder.trip_name}
            </h1>
            <p className="text-xs text-slate-400">
              {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </p>
          </div>
          <Button
            onClick={openNewEntry}
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 rounded-xl h-9 px-3 text-sm"
            data-testid="add-entry-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Entry
          </Button>
        </div>

        {entries.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              No entries yet for this trip
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="journal-entries-list">
            {entries.map((entry) => (
              <div
                key={entry.entry_id}
                className="bg-slate-900/70 rounded-xl border border-white/5 overflow-hidden"
                data-testid={`journal-entry-${entry.entry_id}`}
              >
                {/* Photos strip */}
                {entry.photos?.length > 0 && (
                  <div className="flex gap-1 overflow-x-auto p-2 pb-0">
                    {entry.photos.map((photo, i) => (
                      <img
                        key={i}
                        src={photo}
                        alt=""
                        className="w-24 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    ))}
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-white truncate">
                        {entry.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-3 h-3 text-slate-500" />
                        <span className="text-xs text-slate-400">
                          {entry.date}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEditEntry(entry)}
                        className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-amber-400 transition-colors"
                        data-testid={`edit-entry-${entry.entry_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(entry.entry_id)}
                        className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-red-400 transition-colors"
                        data-testid={`delete-entry-${entry.entry_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {entry.content && (
                    <p className="text-sm text-slate-300 mt-3 whitespace-pre-wrap leading-relaxed line-clamp-4">
                      {entry.content}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <EditorDialog
        open={showEditor}
        onClose={() => setShowEditor(false)}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        saving={saving}
        uploading={uploading}
        editingEntry={editingEntry}
        fileInputRef={fileInputRef}
        handlePhotoUpload={handlePhotoUpload}
        removePhoto={removePhoto}
        folders={folders}
      />
    </div>
  );
};

// ── Editor Dialog Component ──
const EditorDialog = ({
  open,
  onClose,
  form,
  setForm,
  onSave,
  saving,
  uploading,
  editingEntry,
  fileInputRef,
  handlePhotoUpload,
  removePhoto,
  folders,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#141c2e] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="editor-title">
            {editingEntry ? "Edit Entry" : "New Journal Entry"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Trip name / folder */}
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Trip / Folder</Label>
            <Select
              value={form.trip_id || form.trip_name || "__custom__"}
              onValueChange={(val) => {
                if (val === "__custom__") {
                  setForm((p) => ({
                    ...p,
                    trip_id: null,
                    trip_name: "",
                  }));
                } else {
                  const folder = folders.find(
                    (f) => f.trip_id === val || f.trip_name === val
                  );
                  if (folder) {
                    setForm((p) => ({
                      ...p,
                      trip_id: folder.trip_id,
                      trip_name: folder.trip_name,
                    }));
                  }
                }
              }}
            >
              <SelectTrigger
                className="w-full h-10 rounded-lg bg-white/5 border-white/10 text-white text-sm"
                data-testid="entry-trip-select"
              >
                <SelectValue placeholder="Select trip folder" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a2440] border-white/10 text-white">
                <SelectItem value="__custom__" className="text-amber-400 focus:bg-white/10 focus:text-amber-300">
                  + New trip folder
                </SelectItem>
                {folders.map((f, i) => (
                  <SelectItem
                    key={i}
                    value={f.trip_id || f.trip_name}
                    className="text-white focus:bg-white/10 focus:text-white"
                  >
                    {f.trip_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!form.trip_id && (
              <Input
                placeholder="Enter trip name"
                value={form.trip_name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, trip_name: e.target.value }))
                }
                className="bg-white/5 border-white/10 text-white h-10"
                data-testid="entry-trip-name"
              />
            )}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Title</Label>
            <Input
              placeholder="Day 1: Arrival in Tokyo"
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              className="bg-white/5 border-white/10 text-white h-10"
              data-testid="entry-title"
            />
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) =>
                setForm((p) => ({ ...p, date: e.target.value }))
              }
              className="bg-white/5 border-white/10 text-white h-10"
              data-testid="entry-date"
            />
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <Label className="text-slate-400 text-xs">Journal Entry</Label>
            <textarea
              placeholder="Write about your day..."
              value={form.content}
              onChange={(e) =>
                setForm((p) => ({ ...p, content: e.target.value }))
              }
              rows={6}
              className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 resize-y placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              data-testid="entry-content"
            />
          </div>

          {/* Photos */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-400 text-xs">Photos</Label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                data-testid="add-photo-btn"
              >
                {uploading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <ImagePlus className="w-3 h-3" />
                )}
                Add Photo
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
              />
            </div>
            {form.photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {form.photos.map((photo, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={photo}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-white/10 text-slate-300 hover:bg-white/5 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-xl"
              data-testid="save-entry-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingEntry ? (
                "Update"
              ) : (
                "Save Entry"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Journal;
